import React, { useState, useEffect, useMemo, useRef } from "react";
import "./table.less";
import Pagination from "../../BP2Components/Pagination/Pagination";
import { Empty, Spin } from "../../BP2Components";
import { CompactTable } from "@table-library/react-table-library/compact";
import { useTheme } from "@table-library/react-table-library/theme";
import { getTheme } from "@table-library/react-table-library/baseline";
import { TableScripts } from "./tableScripts";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { toggleAllRows, toggleRow } from "../../../appRedux/actions/SelectedRows";
import { useGridNavigation } from "../../../util/useGridNavigation";
import { useFormV3 } from "../../../context/FormV3Context/FormV3Context";
import { useTableDropdownMenu } from "./useTableDropdownMenu";
import { useTableInlineEdit, getRecordId } from "./useTableInlineEdit";

export const TableComponent = ({
   listId,
   rowData,
   formId,
   colData,
   actions,
   loadingPage,
   loadingTable,
   page,
   showPager,
   itemsPerPage,
   totalItems,
   splitForPages,
   onPageChange,
   onCustomEvent,
   filter,
   addRecordBtn = false,
   addRecordCallback,
   withCheckbox,
}) => {
   const [columns, setColumns] = useState([]);
   const [rows, setRows] = useState();
   const tableRootRef = useRef(null);
   const navRef = useRef(null);
   const editedValuesRef = useRef({});

   const dispatch = useDispatch();
   const selectedRowIds = useSelector((state) => state.selectedRows.selectedRowIds);
   const intl = useIntl();

   const handleRowCheckboxChange = (row, checked) => dispatch(toggleRow(row.ID, checked));

   const handleHeaderCheckboxChange = (checked, rowsOnCurrentPage) =>
      dispatch(
         toggleAllRows(
            rowsOnCurrentPage.map((row) => row.ID),
            checked,
         ),
      );

   const renderTable = () => {
      let currentRows = rowData;

      if (splitForPages === true) {
         const firstIndex = page * itemsPerPage - itemsPerPage;
         const calcLastIndex = firstIndex + itemsPerPage;
         const lastIndex = calcLastIndex > rowData?.length ? rowData?.length : calcLastIndex;
         currentRows = rowData.slice(firstIndex, lastIndex);
      }

      if (Object.keys(editedValuesRef.current).length > 0) {
         currentRows = currentRows.map((row) => {
            const recordId = getRecordId(row);
            const overrides = editedValuesRef.current[recordId];
            return overrides ? { ...row, ...overrides } : row;
         });
      }

      const colsAndRows = TableScripts.prepareTableData(
         colData,
         currentRows,
         filter,
         actions,
         onCustomEvent,
         addRecordBtn,
         intl,
         addRecordCallback,
         withCheckbox,
         handleRowCheckboxChange,
         handleHeaderCheckboxChange,
         selectedRowIds,
      );
      setColumns(colsAndRows.preparedColumns);
      setRows(colsAndRows.preparedRows);
   };

   useEffect(() => {
      editedValuesRef.current = {};
   }, [rowData]);

   useEffect(() => {
      renderTable();
   }, [
      rowData,
      colData,
      page,
      actions,
      filter,
      intl,
      splitForPages,
      itemsPerPage,
      onCustomEvent,
      addRecordBtn,
      addRecordCallback,
      selectedRowIds,
   ]); // eslint-disable-line react-hooks/exhaustive-deps

   const visibleColumns = useMemo(
      () =>
         (columns ?? []).filter(
            (col) => !(col.hide === true || col.hide === "1" || col.hide === 1),
         ),
      [columns],
   );

   const actionColIndex = useMemo(
      () => (visibleColumns ?? []).findIndex((col) => col?.id === "api_recordActions"),
      [visibleColumns],
   );

   const rowsCount = rows?.nodes?.length ?? 0;
   const colsCount = visibleColumns.length ?? 0;

   const theme = useTheme([
      getTheme(),
      {
         Table: `--data-table-library_grid-template-columns: ${TableScripts.getColumnWidths(visibleColumns)};`,
         HeaderCell: `text-align: center;`,
      },
   ]);

   let formV3Ctx = null;
   try {
      formV3Ctx = useFormV3();
   } catch {
      formV3Ctx = null;
   }

   const { isMenuOpen, closeMenu, isInAllowedPortal, onToggleActions } = useTableDropdownMenu({
      tableRootRef,
      actionColIndex,
      navRef,
   });

   const { editing, handleEnterEdit, decoratedColumns } = useTableInlineEdit({
      formId,
      listId,
      rows,
      setRows,
      visibleColumns,
      tableRootRef,
      navRef,
      isInAllowedPortal,
      intl,
      formV3Ctx,
      editedValuesRef,
   });

   const nav = useGridNavigation({
      rowsCount,
      colsCount,
      isMenuOpen,
      closeOpenMenu: closeMenu,
      onToggleActions,
      onEnterEditRequest: handleEnterEdit,
      isEditing: !!editing,
   });
   navRef.current = nav;

   // Autofocus the first cell in the first grid on the page
   useEffect(() => {
      const root = tableRootRef.current;
      if (!root || !rowsCount || !colsCount) return;
      if (window.__firstGridFocusDone) return;

      const firstGrid = document.querySelector('[data-table-widget="true"]');
      if (firstGrid !== root) return;

      const col = visibleColumns.findIndex((c) => c?.id !== "api_recordActions");
      const firstCol = col === -1 ? 0 : col;

      let tries = 0;
      const tick = () => {
         const cell = root.querySelector(
            `[data-grid-cell="true"][data-row="0"][data-col="${firstCol}"]`,
         );
         if (cell) {
            navRef.current?.setActive?.({ row: 0, col: firstCol });
            navRef.current?.focusCell?.(0, firstCol);
            window.__firstGridFocusDone = true;
            return;
         }
         if (++tries < 40) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
   }, [rowsCount, colsCount, visibleColumns]); // eslint-disable-line react-hooks/exhaustive-deps

   // Keep highlight when focus moves into the dropdown portal
   const navProps = nav.getTableProps();
   const stableNavProps = {
      ...navProps,
      onBlur: (e) => {
         const ddRoot = document.getElementById("dropdown-root");
         if (ddRoot && ddRoot.contains(e.relatedTarget)) {
            e.preventDefault?.();
            e.stopPropagation?.();
            return;
         }
         navProps.onBlur?.(e);
      },
   };

   return (
      <div
         ref={tableRootRef}
         className={`bp2-table-component-container add-record-btn-${actions ? addRecordBtn : "false"}`}
         data-table-widget="true"
         data-table-id={String(listId ?? "table")}
      >
         {visibleColumns?.length > 0 && rows && !loadingTable ? (
            <div className="table-component" style={loadingPage ? { opacity: 0.5 } : undefined}>
               <div {...stableNavProps}>
                  <CompactTable
                     columns={decoratedColumns}
                     data={rows}
                     theme={theme}
                     layout={{ custom: true, horizontalScroll: true, fixedHeader: true }}
                  />
               </div>
               {!rows.nodes.length && <Empty />}
            </div>
         ) : null}

         {showPager &&
         itemsPerPage &&
         page &&
         Array.isArray(rowData) &&
         rowData?.length &&
         !loadingTable ? (
            <Pagination
               defaultPageSize={itemsPerPage}
               onChange={(p) => onPageChange(p)}
               totalItems={totalItems ?? rowData.length}
               initialPage={page}
            />
         ) : null}

         {(loadingPage || loadingTable) && <Spin className="bp2-table-spinner" />}
      </div>
   );
};
